// Client-side bill/receipt image compression.
//
// Phone camera photos of bills are routinely 3-8MB; embedding several of
// those into a treasurer PDF report (2-per-page) bloats it fast and is slow
// to upload. Non-image attachments (PDF/Word/Excel) are left untouched --
// there's nothing safe to re-encode there.

const DEFAULT_MAX_DIMENSION = 1600; // px, longest side -- plenty for a legible receipt
const DEFAULT_QUALITY = 0.72;
const SKIP_BELOW_BYTES = 300 * 1024; // don't bother re-encoding already-small images

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

/**
 * Resize + re-encode an image File to shrink upload size. Returns the
 * original file unchanged for non-images, already-small images, or on any
 * decode/encode failure (never blocks an upload on a compression bug).
 */
export async function compressBillImage(file, opts = {}) {
  if (!file || !file.type || !file.type.startsWith('image/')) return file;
  if (file.type === 'image/svg+xml') return file; // vector, nothing to shrink
  if (file.size <= SKIP_BELOW_BYTES) return file;

  const maxDimension = opts.maxDimension || DEFAULT_MAX_DIMENSION;
  const quality = opts.quality || DEFAULT_QUALITY;

  try {
    const img = await loadImage(file);
    const scale = Math.min(1, maxDimension / Math.max(img.width, img.height));
    const width = Math.max(1, Math.round(img.width * scale));
    const height = Math.max(1, Math.round(img.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', quality)
    );
    if (!blob || blob.size >= file.size) return file; // compression didn't help; keep original

    const newName = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], newName, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return file; // never block an upload because compression failed
  }
}

export function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
